import { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Loader2 } from "lucide-react";
import { toast } from "@/components/ui/sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { CONTACT } from "@/constants/contact";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const emptyForm = {
  name: "",
  email: "",
  phone: "",
  vehicle: "",
  year: "",
  mileage: "",
  message: "",
};

export const DepotVenteForm = () => {
  const [form, setForm] = useState(emptyForm);
  const [consent, setConsent] = useState(false);
  const [sending, setSending] = useState(false);

  const set = (key) => (e) => setForm({ ...form, [key]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!consent) {
      toast.error("Consentement requis", {
        description: "Veuillez accepter l'utilisation de vos données pour traiter votre demande.",
      });
      return;
    }
    setSending(true);
    try {
      await axios.post(`${API}/depot-vente`, { ...form, consent });
      const waText = [
        "Bonjour MECA AUTO CLEAN 01, je souhaite confier mon véhicule en dépôt-vente.",
        `Nom : ${form.name}`,
        `Téléphone : ${form.phone}`,
        `E-mail : ${form.email}`,
        `Véhicule : ${form.vehicle}`,
        form.year ? `Année : ${form.year}` : null,
        form.mileage ? `Kilométrage : ${form.mileage}` : null,
        form.message ? `Message : ${form.message}` : null,
      ]
        .filter(Boolean)
        .join("\n");
      window.open(
        `https://wa.me/${CONTACT.whatsappNumber}?text=${encodeURIComponent(waText)}`,
        "_blank",
      );
      toast.success("Demande envoyée", {
        description:
          "WhatsApp s'ouvre pour transmettre votre projet de vente au garage. Une copie est aussi envoyée par e-mail.",
      });
      setForm(emptyForm);
      setConsent(false);
    } catch {
      toast.error("Envoi impossible", {
        description: `Réessayez dans un instant ou appelez le garage au ${CONTACT.phoneDisplay}.`,
      });
    } finally {
      setSending(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} data-testid="depot-vente-form" className="space-y-4">
      <div className="grid gap-4 sm:grid-cols-2">
        <Input
          required
          placeholder="Votre nom *"
          value={form.name}
          onChange={set("name")}
          data-testid="depot-vente-name-input"
          aria-label="Votre nom"
        />
        <Input
          required
          type="tel"
          placeholder="Votre téléphone *"
          value={form.phone}
          onChange={set("phone")}
          data-testid="depot-vente-phone-input"
          aria-label="Votre téléphone"
        />
      </div>
      <Input
        required
        type="email"
        placeholder="Votre e-mail *"
        value={form.email}
        onChange={set("email")}
        data-testid="depot-vente-email-input"
        aria-label="Votre e-mail"
      />
      <div className="grid gap-4 sm:grid-cols-3">
        <Input
          required
          placeholder="Véhicule *"
          value={form.vehicle}
          onChange={set("vehicle")}
          data-testid="depot-vente-vehicle-input"
          aria-label="Véhicule"
        />
        <Input
          placeholder="Année"
          value={form.year}
          onChange={set("year")}
          data-testid="depot-vente-year-input"
          aria-label="Année"
        />
        <Input
          placeholder="Kilométrage"
          value={form.mileage}
          onChange={set("mileage")}
          data-testid="depot-vente-mileage-input"
          aria-label="Kilométrage"
        />
      </div>
      <Textarea
        rows={4}
        placeholder="Historique d'entretien, état général, vos attentes..."
        value={form.message}
        onChange={set("message")}
        data-testid="depot-vente-message-input"
        aria-label="Votre message"
      />
      <label className="flex items-start gap-3 text-sm text-muted-foreground">
        <Checkbox
          checked={consent}
          onCheckedChange={(v) => setConsent(v === true)}
          data-testid="depot-vente-consent-checkbox"
          aria-label="Consentement au traitement des données"
          className="mt-0.5"
        />
        <span>
          J'accepte que mes données soient utilisées pour traiter ma demande,
          conformément à la{" "}
          <Link
            to="/politique-de-confidentialite"
            data-testid="depot-vente-privacy-link"
            className="text-primary underline-offset-4 hover:underline"
          >
            politique de confidentialité
          </Link>
          . *
        </span>
      </label>
      <Button
        type="submit"
        disabled={sending}
        data-testid="depot-vente-submit-button"
        className="w-full sm:w-auto"
      >
        {sending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
        Envoyer ma demande
      </Button>
    </form>
  );
};
